<script setup lang="ts">
import {

  Menu,
  User,
  ChevronDown,
  ChevronRight,
  LogOut,
  X,
  Home,
  MessageCircle,
  Mic,
  LayoutDashboard,
  Coins,
  TrendingUp,
  Folder,
  Users,
  UserCog,
  FileText,
  Building2,
  Building,
  Landmark,
  Package,
  Cpu,
  MessagesSquare,
  Ticket,
  Inbox,
  UserCheck,
  FileSignature,
  Monitor,
  Stamp,
  LifeBuoy,
  Briefcase,
  ClipboardList,
  AudioLines,
  ToggleRight,
  ChartColumn,
  BrainCircuit,
} from "@lucide/vue";
import {
  useSessionStore,
} from "~/stores/session";
import {
  useCustomsCheckStore,
} from "~/stores/customsCheck";
import {
  HEADER_MENU_POPPER_OPTIONS, headerMenuWidth,
} from "~/utils/header-menu";
import {
  useSupportRequestCount,
} from "~/composables/useSupportRequestCount";

const route = useRoute();
const {$t} = useNuxtApp();
const sessionStore = useSessionStore();
const apiFetch = useApiFetch();
const customsCheckStore = useCustomsCheckStore();
const {
  count: actionableAccountRequestCount,
  load: loadActionableAccountRequestCount,
  reset: resetActionableAccountRequestCount,
} = useAccountRequestCount();
const {
  count: unreadSupportRequestCount,
  load: loadUnreadSupportRequestCount,
  reset: resetUnreadSupportRequestCount,
} = useSupportRequestCount();
const {
  count: pendingApplicantRequestCount,
  load: loadPendingApplicantRequestCount,
  reset: resetPendingApplicantRequestCount,
} = useApplicantRequestCount();

const drawerVisible = ref(false);
const accountMenuVisible = ref(false);
const legalNotesExpanded = ref(false);
const stoppingImpersonation = ref(false);
let imageMigrationTriggeredForUser: string | null = null;

// Marketing website base URL — used for the AGB/Datenschutz/Impressum links
// in the account menu. Falls back to a relative path in local dev if unset.
const runtimeConfig = useRuntimeConfig();
const websiteUrl = computed(() => runtimeConfig.public.websiteUrl || "");

const isLoginRoute = computed(() => {
  const path = route.path;
  return path === "/" || path === "/login" || path.startsWith("/auth/");
});

const navIcons = {
  Home,
  MessageCircle,
  Mic,
  Stamp,
  LayoutDashboard,
  Coins,
  TrendingUp,
  Folder,
  Users,
  UserCog,
  FileText,
  Building2,
  Package,
  Cpu,
  MessagesSquare,
  Ticket,
  Inbox,
  UserCheck,
  FileSignature,
  Monitor,
  LifeBuoy,
  Briefcase,
  ClipboardList,
  Building,
  Landmark,
  AudioLines,
  ToggleRight,
  ChartColumn,
  BrainCircuit,
};

type NavItem = {
  label?: string;
  icon?: keyof typeof navIcons;
  route?: string;
  type?: "section" | "divider";
  roles?: string[];
  group?: string;
  /** Gated on a runtime flag rather than a role. */
  requiresAccess?: "customsCheck";
  /** Learner-only entries; employers have no training area. */
  hiddenForEmployers?: boolean;
  /** Sub-items for collapsible parent menu entries. */
  children?: NavItem[];
};

const allNavItems: NavItem[] = [
  {label: "NAV.USER_AREA", type: "section", group: "user", hiddenForEmployers: true},
  {label: "NAV.HOME", icon: "Home", route: "/home", group: "user", hiddenForEmployers: true},
  {label: "NAV.INTERVIEW_EXERCISE", icon: "MessageCircle", route: "/exercise/interview", group: "user", hiddenForEmployers: true},
  {label: "NAV.DIALOG_TRAINING", icon: "MessagesSquare", route: "/exercise/dialog-training", group: "user", hiddenForEmployers: true},
  {label: "NAV.CUSTOMS_CHECK", icon: "Stamp", route: "/exercise/customs-check", group: "user", hiddenForEmployers: true, requiresAccess: "customsCheck"},
  {label: "NAV.MY_PERFORMANCE", icon: "TrendingUp", route: "/my-performance", group: "user", hiddenForEmployers: true},
  {label: "NAV.MY_CREDITS_USAGE", icon: "Coins", route: "/my-credits-usage", group: "user", hiddenForEmployers: true},
  {type: "divider", group: "tenant-manager"},
  {label: "NAV.TENANT_MANAGER_AREA", type: "section", group: "tenant-manager", roles: ["TENANT_MANAGER", "TENANT_ADMIN", "AIL_ADMIN"]},
  {label: "NAV.DASHBOARD", icon: "LayoutDashboard", route: "/tenant-manager-dashboard", group: "tenant-manager", roles: ["TENANT_MANAGER", "TENANT_ADMIN", "AIL_ADMIN"]},
  {label: "NAV.USER_PERFORMANCE", icon: "TrendingUp", route: "/user-performances", group: "tenant-manager", roles: ["TENANT_MANAGER", "TENANT_ADMIN", "AIL_ADMIN"]},
  {label: "NAV.USER_GROUPS", icon: "Users", route: "/user-performances/user-group-management", group: "tenant-manager", roles: ["TENANT_MANAGER", "TENANT_ADMIN", "AIL_ADMIN"]},
  {type: "divider", group: "tenant-admin"},
  {label: "NAV.TENANT_ADMIN_AREA", type: "section", group: "tenant-admin", roles: ["TENANT_ADMIN", "AIL_ADMIN"]},
  {label: "NAV.ADMIN_DASHBOARD", icon: "LayoutDashboard", route: "/tenant-admin-dashboard", group: "tenant-admin", roles: ["TENANT_ADMIN", "AIL_ADMIN"]},
  {label: "NAV.TENANT_USERS", icon: "UserCog", route: "/tenant-admin/user-management", group: "tenant-admin", roles: ["TENANT_ADMIN", "AIL_ADMIN"]},
  {label: "NAV.USAGE_LICENSES", icon: "FileText", route: "/tenant-admin/usage-license-overview", group: "tenant-admin", roles: ["TENANT_ADMIN", "AIL_ADMIN"]},
  {label: "NAV.ORGANIZATION_PROFILE", icon: "Building2", route: "/tenant-admin/organization-profile", group: "tenant-admin", roles: ["TENANT_ADMIN", "AIL_ADMIN"]},
  {type: "divider", group: "employer"},
  {label: "NAV.EMPLOYER_AREA", type: "section", group: "employer", roles: ["EMPLOYER_ADMIN", "EMPLOYER_MANAGER", "AIL_ADMIN"]},
  {label: "NAV.EMPLOYER_DASHBOARD", icon: "LayoutDashboard", route: "/employer/dashboard", group: "employer", roles: ["EMPLOYER_ADMIN", "EMPLOYER_MANAGER", "AIL_ADMIN"]},
  {label: "NAV.JOB_OFFERS", icon: "ClipboardList", route: "/employer/job-offers", group: "employer", roles: ["EMPLOYER_ADMIN", "EMPLOYER_MANAGER", "AIL_ADMIN"]},
  {label: "NAV.EMPLOYER_PROFILE", icon: "Briefcase", route: "/employer/profile", group: "employer", roles: ["EMPLOYER_ADMIN", "EMPLOYER_MANAGER", "AIL_ADMIN"]},
  {type: "divider", group: "admin"},
  {label: "NAV.ADMIN_AREA", type: "section", group: "admin", roles: ["AIL_ADMIN"]},
  {label: "NAV.USER_MANAGEMENT", icon: "UserCog", route: "/admin/user-management", group: "admin", roles: ["AIL_ADMIN"]},
  {label: "NAV.TENANT_MANAGEMENT", icon: "Building", route: "/admin/tenant-management", group: "admin", roles: ["AIL_ADMIN"]},
  {label: "NAV.REQUESTS",
    icon: "Inbox",
    group: "admin",
    roles: ["AIL_ADMIN"],
    children: [
      {label: "NAV.SUPPORT_REQUESTS", icon: "LifeBuoy", route: "/admin/requests/support", group: "admin", roles: ["AIL_ADMIN"]},
      {label: "NAV.ACCOUNT_REQUESTS", icon: "Landmark", route: "/admin/requests/organization", group: "admin", roles: ["AIL_ADMIN"]},
      {label: "NAV.APPLICANT_REQUESTS", icon: "UserCheck", route: "/admin/requests/applicant", group: "admin", roles: ["AIL_ADMIN"]},
      {label: "NAV.INTRODUCTION_REVIEWS", icon: "AudioLines", route: "/admin/requests/introductions", group: "admin", roles: ["AIL_ADMIN"]},
    ]},
  {label: "NAV.EMPLOYER_JOB_OFFERS", icon: "Briefcase", route: "/admin/job-offers", group: "admin", roles: ["AIL_ADMIN"]},
  {label: "NAV.PLANS", icon: "Package", route: "/admin/plans", group: "admin", roles: ["AIL_ADMIN"]},
  {label: "NAV.LEGAL_AGREEMENTS", icon: "FileSignature", route: "/admin/agreements", group: "admin", roles: ["AIL_ADMIN"]},
  {label: "NAV.INTERVIEW_AGENT", icon: "Cpu", route: "/admin/interview-agent", group: "admin", roles: ["AIL_ADMIN"]},
  {label: "NAV.DIALOG_TOPICS", icon: "MessagesSquare", route: "/admin/dialog-topics", group: "admin", roles: ["AIL_ADMIN"]},
  {label: "NAV.ADMIN_CUSTOMS_CHECK", icon: "Stamp", route: "/admin/customs-check", group: "admin", roles: ["AIL_ADMIN"]},
  {label: "NAV.SYSTEM_MONITORING", icon: "Monitor", route: "/admin/system-monitoring", group: "admin", roles: ["AIL_ADMIN"]},
  {label: "NAV.AI_USAGE", icon: "ChartColumn", route: "/admin/ai-usage", group: "admin", roles: ["AIL_ADMIN"]},
  {label: "NAV.AI_MODELS", icon: "BrainCircuit", route: "/admin/ai-models", group: "admin", roles: ["AIL_ADMIN"]},
  {label: "NAV.FEATURE_FLAGS", icon: "ToggleRight", route: "/admin/feature-flags", group: "admin", roles: ["AIL_ADMIN"]},
];

const hasRole = (roles?: string[]): boolean => {
  if (!roles || roles.length === 0) {
    return true;
  }
  const userRoles = sessionStore.userRoles;
  return roles.some((r) => userRoles.includes(r));
};

const isEmployerAccount = computed(() => sessionStore.userRoles.includes("EMPLOYER_ADMIN")
  || sessionStore.userRoles.includes("EMPLOYER_MANAGER"));

const logoHomeRoute = computed(() => {
  const roles = sessionStore.userRoles;
  if (roles.includes("AIL_ADMIN")) {
    return "/admin/user-management";
  }
  if (roles.includes("TENANT_ADMIN")) {
    return "/tenant-admin-dashboard";
  }
  if (roles.includes("TENANT_MANAGER")) {
    return "/tenant-manager-dashboard";
  }
  if (roles.includes("EMPLOYER_ADMIN") || roles.includes("EMPLOYER_MANAGER")) {
    return "/employer/dashboard";
  }
  return "/home";
});

const isVisible = (item: NavItem): boolean => {
  if (!hasRole(item.roles)) {
    return false;
  }
  if (item.hiddenForEmployers && isEmployerAccount.value) {
    return false;
  }
  if (item.requiresAccess === "customsCheck") {
    return customsCheckStore.hasAccess === true;
  }
  return true;
};

const isAccountRequestItem = (item: NavItem): boolean =>
  item.route === "/admin/requests/organization" || item.route === "/admin/account-requests";
const isSupportRequestItem = (item: NavItem): boolean =>
  item.route === "/admin/requests/support" || item.route === "/admin/support-requests";
const isApplicantRequestItem = (item: NavItem): boolean =>
  item.route === "/admin/requests/applicant" || item.route === "/admin/applicant-requests";

const isRequestsParent = (item: NavItem): boolean => item.label === "NAV.REQUESTS";

const isEmployerWorkspaceNavActive = (item: NavItem): boolean => item.route === "/employer/dashboard"
  && ["/employer/dashboard", "/employer/watchlist", "/employer/applicant-requests"].includes(route.path);

const totalRequestsBadge = computed(() =>
  actionableAccountRequestCount.value + unreadSupportRequestCount.value + pendingApplicantRequestCount.value);

const expandedMenus = ref<Set<string>>(new Set());
const toggleMenu = (label: string) => {
  if (expandedMenus.value.has(label)) {
    expandedMenus.value.delete(label);
  } else {
    expandedMenus.value.add(label);
  }
};
const isMenuExpanded = (label: string) => expandedMenus.value.has(label);

const autoExpandOnRoute = () => {
  for (const item of allNavItems) {
    if (item.children && item.label) {
      const matches = item.children.some((child) => child.route && route.path.startsWith(child.route));
      if (matches) {
        expandedMenus.value.add(item.label);
      }
    }
  }
};
autoExpandOnRoute();
watch(
  () => route.path,
  () => autoExpandOnRoute(),
);

const isReadOnlyEmployerSection = (item: NavItem): boolean => item.label === "NAV.EMPLOYER_AREA"
  && sessionStore.userRoles.includes("AIL_ADMIN")
  && !sessionStore.userRoles.includes("EMPLOYER_ADMIN")
  && !sessionStore.userRoles.includes("EMPLOYER_MANAGER");

const navMenuItems = computed(() => {
  const isPending = sessionStore.user?.status === "pending";
  const isRouteVisible = (item: NavItem) => {
    if (!isVisible(item)) {
      return false;
    }
    if (isPending && item.route !== "/home" && item.route !== "/my-credits-usage") {
      return false;
    }
    return true;
  };
  const visibleGroups = new Set<string>();
  for (const item of allNavItems) {
    if (item.children) {
      const hasVisibleChild = item.children.some((child) => child.route && isRouteVisible(child));
      if (hasVisibleChild && item.group) {
        visibleGroups.add(item.group);
      }
      continue;
    }
    if (item.route && isRouteVisible(item)) {
      if (item.group) {
        visibleGroups.add(item.group);
      }
    }
  }
  const items = allNavItems.filter((item) => {
    if (item.children) {
      const hasVisibleChild = item.children.some((child) => child.route && isRouteVisible(child));
      if (!hasVisibleChild) {
        return false;
      }
      return !item.group || visibleGroups.has(item.group) || isVisible(item);
    }
    if (item.route) {
      return isRouteVisible(item);
    }
    if (item.group) {
      return visibleGroups.has(item.group);
    }
    return true;
  });

  // A divider separates two groups. Once the group above it is hidden — as the
  // learner area is for employers — it would sit at the very top with nothing to
  // separate, so drop any divider that no longer follows an entry.
  return items.filter((item, index) => item.type !== "divider"
    || items.slice(0, index).some((previous) => previous.type !== "divider"));
});

// Probed once the session is ready, and again on login/logout, so the menu
// reflects the current user rather than whoever was signed in before.
watch(
  () => [sessionStore.ready, sessionStore.isAuthenticated, sessionStore.user?.sub] as const,
  ([ready, authenticated]) => {
    if (!ready) {
      return;
    }
    if (authenticated) {
      customsCheckStore.fetchAccess();
      if (sessionStore.userRoles.includes("AIL_ADMIN")) {
        const userId = sessionStore.user?.sub ?? "admin";
        if (imageMigrationTriggeredForUser !== userId) {
          imageMigrationTriggeredForUser = userId;
          // The request remains open while the server performs the one-time
          // job, but it is deliberately not awaited so navigation stays fast.
          void apiFetch("/admin/migrations/images-to-webp/auto", {method: "POST"})
            .catch((error) => console.warn("[ImageMigration] Automatic WebP migration failed", error));
        }
        void loadActionableAccountRequestCount(true);
        void loadUnreadSupportRequestCount(true);
        void loadPendingApplicantRequestCount(true);
      } else {
        resetActionableAccountRequestCount();
        resetUnreadSupportRequestCount();
        resetPendingApplicantRequestCount();
      }
    } else {
      imageMigrationTriggeredForUser = null;
      customsCheckStore.hasAccess = null;
      resetActionableAccountRequestCount();
      resetUnreadSupportRequestCount();
      resetPendingApplicantRequestCount();
    }
  },
  {immediate: true},
);

watch(
  () => route.fullPath,
  () => {
    if (sessionStore.ready && sessionStore.isAuthenticated && sessionStore.userRoles.includes("AIL_ADMIN")) {
      void loadActionableAccountRequestCount(true);
      void loadUnreadSupportRequestCount(true);
      void loadPendingApplicantRequestCount(true);
    }
  },
);

const handleLogout = () => {
  accountMenuVisible.value = false;
  sessionStore.logout();
};

const handleStopImpersonation = async () => {
  if (stoppingImpersonation.value) {
    return;
  }

  stoppingImpersonation.value = true;
  accountMenuVisible.value = false;

  try {
    await sessionStore.stopImpersonation();
    await navigateTo("/home");
  } finally {
    stoppingImpersonation.value = false;
  }
};

const closeDrawer = () => {
  drawerVisible.value = false;
};
</script>

<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Loading state -->
    <div
      v-if="!sessionStore.ready"
      class="flex items-center justify-center min-h-screen"
    >
      <base-loader
        :size="36"
        class="text-primary-500"
      />
    </div>

    <!-- Main layout -->
    <template v-else>
      <!-- Header (hidden on login routes) -->
      <header
        v-if="!isLoginRoute"
        class="fixed top-0 left-0 right-0 h-[60px] bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 z-50"
      >
        <div class="flex items-center justify-between h-full">
          <!-- Left: Menu button + Logo + Title -->
          <div class="flex items-center">
            <el-button
              text
              class="xl:!hidden"
              @click="drawerVisible = true"
            >
              <Menu :size="24" />
            </el-button>
            <NuxtLink
              :to="logoHomeRoute"
              class="flex items-center gap-2 xl:pl-5"
            >
              <AilLogo :size="32" />
              <h1 class="hidden text-xl font-semibold text-gray-900 dark:text-white min-[480px]:block">
                {{ $t('COMMON.APP_NAME') }}
              </h1>
            </NuxtLink>
          </div>

          <!-- Right: Theme toggle + Account menu -->
          <div class="flex items-center">
            <language-switcher />

            <PersonalCreditsBlock v-if="!isEmployerAccount" />

            <el-popover
              v-model:visible="accountMenuVisible"
              placement="bottom-end"
              :width="headerMenuWidth(320)"
              :popper-options="HEADER_MENU_POPPER_OPTIONS"
              trigger="click"
            >
              <template #reference>
                <el-button
                  text
                  class="!px-2"
                >
                  <User :size="20" />
                  <span class="ml-2 hidden sm:inline">{{ sessionStore.userDisplayName }}</span>
                  <ChevronDown
                    :size="16"
                    class="ml-1"
                  />
                </el-button>
              </template>

              <div class="p-1">
                <div class="flex flex-row gap-x-2 items-center">
                  <div class="min-w-0 flex-1 rounded bg-gray-50/50 px-2 py-2 dark:bg-gray-800/50">
                    <div class="flex min-w-0 flex-col">
                      <p class="break-words font-medium leading-snug text-gray-900 [overflow-wrap:anywhere] dark:text-white">
                        {{ sessionStore.userDisplayName }}
                      </p>

                      <p
                        v-if="sessionStore.userDisplayName !== sessionStore.userEmail"
                        class="break-words text-xs leading-snug text-gray-500 [overflow-wrap:anywhere] dark:text-gray-400"
                      >
                        {{ sessionStore.userEmail }}
                      </p>
                    </div>
                  </div>

                  <theme-toggle variant="switcher" />
                </div>

                <el-divider class="!my-2" />

                <div class="space-y-1">
                  <NuxtLink
                    to="/profile"
                    class="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-100/80 dark:text-gray-200 dark:hover:bg-gray-700/60 rounded-md"
                    @click="accountMenuVisible = false"
                  >
                    <User
                      :size="18"
                      class="text-gray-600 dark:text-gray-300"
                    />
                    <span class="font-normal text-gray-800 dark:text-gray-200">{{ $t('NAV.PROFILE') }}</span>
                  </NuxtLink>

                  <div>
                    <div
                      class="flex cursor-pointer select-none items-center justify-between px-2 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-100/80 dark:text-gray-200 dark:hover:bg-gray-700/60 rounded-md"
                      @click="legalNotesExpanded = !legalNotesExpanded"
                    >
                      <div class="flex items-center gap-2">
                        <FileText
                          :size="18"
                          class="text-gray-600 dark:text-gray-300"
                        />
                        <span class="font-normal text-gray-800 dark:text-gray-200">{{ $t('NAV.LEGAL_NOTES') }}</span>
                      </div>
                      <ChevronRight
                        :size="14"
                        class="text-gray-400 transition-transform duration-200"
                        :class="{ 'rotate-90': legalNotesExpanded }"
                      />
                    </div>

                    <div
                      v-if="legalNotesExpanded"
                      class="mt-0.5 flex flex-col gap-0.5 pl-7 pr-1"
                    >
                      <a
                        :href="`${websiteUrl}/terms`"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="block rounded px-2 py-1 text-xs text-gray-600 transition-colors hover:bg-gray-50 hover:text-primary-600 dark:text-gray-400 dark:hover:bg-gray-700/40 dark:hover:text-primary-400"
                        @click="accountMenuVisible = false"
                      >
                        {{ $t('NAV.TERMS_AND_CONDITIONS') }}
                      </a>

                      <a
                        :href="`${websiteUrl}/privacy`"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="block rounded px-2 py-1 text-xs text-gray-600 transition-colors hover:bg-gray-50 hover:text-primary-600 dark:text-gray-400 dark:hover:bg-gray-700/40 dark:hover:text-primary-400"
                        @click="accountMenuVisible = false"
                      >
                        {{ $t('NAV.PRIVACY_POLICY') }}
                      </a>

                      <a
                        :href="`${websiteUrl}/imprint`"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="block rounded px-2 py-1 text-xs text-gray-600 transition-colors hover:bg-gray-50 hover:text-primary-600 dark:text-gray-400 dark:hover:bg-gray-700/40 dark:hover:text-primary-400"
                        @click="accountMenuVisible = false"
                      >
                        {{ $t('NAV.IMPRINT') }}
                      </a>
                    </div>
                  </div>

                  <ContactSupportMenuItem @click="accountMenuVisible = false" />
                </div>

                <el-divider class="!my-2" />

                <el-button
                  type="danger"
                  plain
                  class="w-full"
                  @click="handleLogout"
                >
                  <LogOut
                    class="mr-2"
                    :size="16"
                  />
                  {{ $t('COMMON.LOGOUT') }}
                </el-button>
              </div>
            </el-popover>
          </div>
        </div>
      </header>

      <!-- Sidebar (desktop) -->
      <aside
        v-if="!isLoginRoute"
        class="hidden xl:block fixed top-[60px] left-0 bottom-0 w-[280px] bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto"
      >
        <nav class="py-4">
          <template
            v-for="(item, index) in navMenuItems"
            :key="index"
          >
            <template
              v-if="item.type === 'section'"
            >
              <div
                v-if="item.label && $t(item.label)"
                class="menu-section-title flex items-center gap-2"
              >
                <span>{{ item.label && $t(item.label) }}</span>
                <span
                  v-if="isReadOnlyEmployerSection(item)"
                  class="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                >
                  {{ $t('NAV.READ_ONLY') }}
                </span>
              </div>
            </template>
            <el-divider
              v-else-if="item.type === 'divider'"
              class="!my-2"
            />
            <template
              v-else-if="item.children"
            >
              <div
                class="flex items-center gap-3 px-4 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                @click="toggleMenu(item.label!)"
              >
                <component
                  :is="item.icon ? navIcons[item.icon] : undefined"
                  :size="18"
                />

                <span class="flex-1">{{ item.label && $t(item.label) }}</span>

                <span
                  v-if="isRequestsParent(item) && totalRequestsBadge > 0"
                  class="inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-semibold leading-none text-white"
                >
                  {{ totalRequestsBadge }}
                </span>

                <ChevronDown
                  :size="16"
                  class="duration-300 -rotate-90"
                  :class="{ '!rotate-0': isMenuExpanded(item.label!) }"
                />
              </div>
              <template v-if="isMenuExpanded(item.label!)">
                <NuxtLink
                  v-for="(child, childIdx) in item.children"
                  :key="childIdx"
                  :to="child.route"
                  class="flex items-center gap-3 pl-11 pr-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer pointer-events-auto"
                  active-class="bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 border-r-2 border-primary-500"
                >
                  <component
                    :is="child.icon ? navIcons[child.icon] : undefined"
                    :size="16"
                  />
                  <span>{{ child.label && $t(child.label) }}</span>
                  <span
                    v-if="isAccountRequestItem(child) && actionableAccountRequestCount > 0"
                    class="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-semibold leading-none text-white"
                  >
                    {{ actionableAccountRequestCount }}
                  </span>
                  <span
                    v-if="isSupportRequestItem(child) && unreadSupportRequestCount > 0"
                    class="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-semibold leading-none text-white"
                  >
                    {{ unreadSupportRequestCount }}
                  </span>
                  <span
                    v-if="isApplicantRequestItem(child) && pendingApplicantRequestCount > 0"
                    class="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-semibold leading-none text-white"
                  >
                    {{ pendingApplicantRequestCount }}
                  </span>
                </NuxtLink>
              </template>
            </template>
            <NuxtLink
              v-else
              :to="item.route"
              class="flex items-center gap-3 px-4 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer pointer-events-auto"
              :class="isEmployerWorkspaceNavActive(item)
                ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 border-r-2 border-primary-500'
                : ''"
              active-class="bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 border-r-2 border-primary-500"
            >
              <component
                :is="item.icon ? navIcons[item.icon] : undefined"
                :size="18"
              />
              <span>{{ item.label && $t(item.label) }}</span>
              <span
                v-if="isAccountRequestItem(item) && actionableAccountRequestCount > 0"
                class="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-semibold leading-none text-white"
              >
                {{ actionableAccountRequestCount }}
              </span>
              <span
                v-if="isSupportRequestItem(item) && unreadSupportRequestCount > 0"
                class="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-semibold leading-none text-white"
              >
                {{ unreadSupportRequestCount }}
              </span>
              <span
                v-if="isApplicantRequestItem(item) && pendingApplicantRequestCount > 0"
                class="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-semibold leading-none text-white"
              >
                {{ pendingApplicantRequestCount }}
              </span>
            </NuxtLink>
          </template>
        </nav>
      </aside>

      <!-- Mobile drawer -->
      <el-drawer
        v-model="drawerVisible"
        direction="ltr"
        :size="300"
        :show-close="false"
        :with-header="false"
        class="mobile-nav-drawer xl:hidden"
      >
        <div class="flex flex-col h-full bg-white dark:bg-gray-800">
          <!-- Compact header strip with close button -->
          <div class="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 px-3 dark:border-gray-700">
            <NuxtLink
              :to="logoHomeRoute"
              class="flex min-w-0 items-center gap-2"
              @click="closeDrawer"
            >
              <AilLogo :size="32" />
              <span class="truncate text-lg font-semibold text-gray-900 dark:text-white">
                {{ $t('COMMON.APP_NAME') }}
              </span>
            </NuxtLink>

            <base-icon-button
              :icon="X"
              :size="16"
              :tooltip="$t('COMMON.CLOSE_BTN')"
              @click="closeDrawer"
            />
          </div>

          <!-- Nav -->
          <nav class="flex-1 overflow-y-auto pb-2">
            <template
              v-for="(item, index) in navMenuItems"
              :key="index"
            >
              <template
                v-if="item.type === 'section'"
              >
                <div
                  v-if="item.label && $t(item.label)"
                  class="menu-section-title flex items-center gap-2"
                >
                  <span>{{ item.label && $t(item.label) }}</span>
                  <span
                    v-if="isReadOnlyEmployerSection(item)"
                    class="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                  >
                    {{ $t('NAV.READ_ONLY') }}
                  </span>
                </div>
              </template>
              <el-divider
                v-else-if="item.type === 'divider'"
                class="!my-2"
              />
              <template
                v-else-if="item.children"
              >
                <div
                  class="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                  @click="toggleMenu(item.label!)"
                >
                  <component
                    :is="item.icon ? navIcons[item.icon] : undefined"
                    :size="20"
                  />

                  <span class="flex-1 text-[15px]">{{ item.label && $t(item.label) }}</span>

                  <span
                    v-if="isRequestsParent(item) && totalRequestsBadge > 0"
                    class="inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-semibold leading-none text-white"
                  >
                    {{ totalRequestsBadge }}
                  </span>

                  <ChevronDown
                    :size="16"
                    class="duration-300 -rotate-90"
                    :class="{ '!rotate-0': isMenuExpanded(item.label!) }"
                  />
                </div>
                <template v-if="isMenuExpanded(item.label!)">
                  <NuxtLink
                    v-for="(child, childIdx) in item.children"
                    :key="childIdx"
                    :to="child.route"
                    class="flex items-center gap-3 pl-11 pr-4 py-2.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 transition-colors cursor-pointer pointer-events-auto"
                    active-class="bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 border-r-2 border-primary-500"
                    @click="closeDrawer"
                  >
                    <component
                      :is="child.icon ? navIcons[child.icon] : undefined"
                      :size="18"
                    />
                    <span>{{ child.label && $t(child.label) }}</span>
                    <span
                      v-if="isAccountRequestItem(child) && actionableAccountRequestCount > 0"
                      class="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-semibold leading-none text-white"
                    >
                      {{ actionableAccountRequestCount }}
                    </span>
                    <span
                      v-if="isSupportRequestItem(child) && unreadSupportRequestCount > 0"
                      class="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-semibold leading-none text-white"
                    >
                      {{ unreadSupportRequestCount }}
                    </span>
                    <span
                      v-if="isApplicantRequestItem(child) && pendingApplicantRequestCount > 0"
                      class="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-semibold leading-none text-white"
                    >
                      {{ pendingApplicantRequestCount }}
                    </span>
                  </NuxtLink>
                </template>
              </template>
              <NuxtLink
                v-else
                :to="item.route"
                class="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 transition-colors cursor-pointer pointer-events-auto"
                :class="isEmployerWorkspaceNavActive(item)
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 border-r-2 border-primary-500'
                  : ''"
                active-class="bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 border-r-2 border-primary-500"
                @click="closeDrawer"
              >
                <component
                  :is="item.icon ? navIcons[item.icon] : undefined"
                  :size="20"
                />
                <span class="text-[15px]">{{ item.label && $t(item.label) }}</span>
                <span
                  v-if="isAccountRequestItem(item) && actionableAccountRequestCount > 0"
                  class="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-semibold leading-none text-white"
                >
                  {{ actionableAccountRequestCount }}
                </span>
                <span
                  v-if="isSupportRequestItem(item) && unreadSupportRequestCount > 0"
                  class="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-semibold leading-none text-white"
                >
                  {{ unreadSupportRequestCount }}
                </span>
                <span
                  v-if="isApplicantRequestItem(item) && pendingApplicantRequestCount > 0"
                  class="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-semibold leading-none text-white"
                >
                  {{ pendingApplicantRequestCount }}
                </span>
              </NuxtLink>
            </template>
          </nav>
        </div>
      </el-drawer>

      <!-- Main content -->
      <main
        :class="[
          'min-h-screen transition-all',
          isLoginRoute ? '' : 'pt-[60px] xl:pl-[280px]',
        ]"
      >
        <div
          v-if="!isLoginRoute && sessionStore.isImpersonating"
          class="sticky top-[60px] z-40 border-b border-amber-200 bg-amber-50/95 px-4 py-3 backdrop-blur dark:border-amber-800/50 dark:bg-amber-950/80"
        >
          <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div class="min-w-0">
              <p class="text-sm font-semibold text-amber-900 dark:text-amber-100">
                You are impersonating {{ sessionStore.userDisplayName }}.
              </p>
              <p class="text-xs text-amber-800/80 dark:text-amber-200/80">
                Started by {{ sessionStore.impersonationActorDisplayName }}
                <span v-if="sessionStore.impersonationActorEmail">({{ sessionStore.impersonationActorEmail }})</span>
              </p>
            </div>
            <el-button
              type="warning"
              plain
              :loading="stoppingImpersonation"
              @click="handleStopImpersonation"
            >
              {{ $t('COMMON.STOP_IMPERSONATION') }}
            </el-button>
          </div>
        </div>
        <slot />
        <ScrollToTopButton />
      </main>
    </template>
  </div>
</template>
